import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type {
  InteroperabilityConfiguration,
  ParameterMapping,
  ErrorHandlingConfig,
} from "./types";
import SelectField from "@/components/common/select";
import {
  useGetCoreServices,
  useGetServiceEndpoints,
} from "@/queries/interoperability/interoperability.api";
import { Renderify } from "renderify-js";
import { ParameterInput } from "./ParameterInput";
import { useGetVariables } from "@/workflow_api/hooks/variables/useGetVariables";
import { Icon } from "@elaniintc/goes-design-system";

interface InteroperabilityConfigFormProps {
  initialConfig?: InteroperabilityConfiguration;
  onConfigChange: (config: InteroperabilityConfiguration) => void;
  onValidationChange: (isValid: boolean) => void;
}

export const InteroperabilityConfigForm: React.FC<
  InteroperabilityConfigFormProps
> = ({ initialConfig, onConfigChange, onValidationChange }) => {
  const [selectedService, setSelectedService] = useState<string>(
    initialConfig?.serviceId || "",
  );
  const [selectedEndpoint, setSelectedEndpoint] = useState<string>(
    initialConfig?.endpointId || "",
  );
  console.log(initialConfig);
  const [parameterValues, setParameterValues] = useState<
    Record<string, string>
  >({});
  console.log(parameterValues);
  const [errorHandling, setErrorHandling] = useState<ErrorHandlingConfig>(
    initialConfig?.errorHandling || {
      enabled: false,
      fallbackAction: "continue",
      retryAttempts: 3,
      retryDelay: 1000,
    },
  );
  const { data: coreServices, isLoading } = useGetCoreServices();
  const { data: endpoints, isLoading: endpointsLoading } =
    useGetServiceEndpoints(selectedService);
  const { variables: nodeVariables, isLoading: isLoadingVariables } =
    useGetVariables();

  const options = useMemo(() => {
    const servicesData = coreServices?.data || {};
    return Object.keys(servicesData).map((key) => ({
      value: key,
      label: key.toUpperCase(),
    }));
  }, [coreServices]);

  const endpointOptions = useMemo(() => {
    return (
      endpoints?.map(
        (endpoint: { id: string; name: string; service: unknown }) => ({
          value: endpoint.id,
          label: endpoint.name,
        }),
      ) || []
    );
  }, [endpoints]);

  const selectedEndpointData = useMemo(() => {
    return endpoints?.find(
      (e: { id: string; name: string; service: unknown }) =>
        e.id === selectedEndpoint,
    );
  }, [endpoints, selectedEndpoint]);

  const inputParameters = useMemo(() => {
    if (!selectedEndpointData?.service?.request?.params) return [];

    return Object.entries(selectedEndpointData.service.request.params).map(
      ([paramName, paramDef]: [string, unknown]) => {
        const def = paramDef as {
          description?: string;
          display_name?: string;
          example?: string;
          optional?: boolean;
          type?: string;
          icon?: string;
        };
        return {
          name: paramName,
          display_name: def.display_name || "",
          description: def.description || "",
          example: def.example || "",
          optional: def.optional || false,
          type: def.type || "string",
          icon: def.icon,
        };
      },
    );
  }, [selectedEndpointData]);

  const outputParameters = useMemo(() => {
    if (!selectedEndpointData?.service?.responses?.["200"]?.data) return [];

    return Object.entries(
      selectedEndpointData.service.responses["200"].data,
    ).map(([paramName, paramDef]: [string, unknown]) => {
      const def = paramDef as {
        description?: string;
        display_name?: string;
        example?: string;
        optional?: boolean;
        type?: string;
        icon?: string;
      };
      return {
        name: paramName,
        display_name: def.display_name || "",
        description: def.description || "",
        example: def.example || "",
        optional: def.optional || false,
        type: def.type || "string",
        icon: def.icon,
      };
    });
  }, [selectedEndpointData]);

  // Helper function to find variable by name and get its ID
  const findVariableIdByName = useCallback(
    (variableName: string): string => {
      if (!variableName || !nodeVariables) return "";
      for (const node of nodeVariables) {
        const variable = node.variables.find((v) => v.name === variableName);
        if (variable) {
          return variable.id;
        }
      }
      return "";
    },
    [nodeVariables],
  );

  // Helper function to check if a value is a variable name
  const isVariableName = useCallback(
    (value: string): boolean => {
      if (!value || !nodeVariables) return false;
      return nodeVariables.some((node) =>
        node.variables.some((v) => v.name === value),
      );
    },
    [nodeVariables],
  );

  // Helper function to find variable name by ID (for backward compatibility)
  const findVariableNameById = useCallback(
    (variableId: string): string => {
      if (!variableId || !nodeVariables) return variableId;
      for (const node of nodeVariables) {
        const variable = node.variables.find((v) => v.id === variableId);
        if (variable) {
          return variable.name;
        }
      }
      return variableId; // Return ID if not found (fallback)
    },
    [nodeVariables],
  );

  // Build configuration object
  const configuration = useMemo((): InteroperabilityConfiguration => {
    const inputMappings: ParameterMapping[] = inputParameters.map((param) => {
      const paramValue = parameterValues[param.name] || "";
      const isVariable = isVariableName(paramValue);
      const variableId = isVariable ? findVariableIdByName(paramValue) : "";
      const variableName = paramValue; // The value is already the name (or direct value)

      return {
        id: `input-${param.name}`,
        parameterId: param.name,
        variableId,
        variableName,
        variableType: param.type,
        isRequired: !param.optional,
      };
    });

    const outputMappings: ParameterMapping[] = outputParameters.map(
      (param) => ({
        id: `output-${param.name}`,
        parameterId: param.name,
        variableId: "", // Outputs don't map to existing variables initially
        variableName: param.name,
        variableType: param.type,
        isRequired: !param.optional,
      }),
    );

    return {
      serviceId: selectedService,
      endpointId: selectedEndpoint,
      inputMappings,
      outputMappings,
      errorHandling,
      isServiceValidated: false, // TODO: Implement service validation
      lastValidationDate: undefined,
    };
  }, [
    selectedService,
    selectedEndpoint,
    inputParameters,
    parameterValues,
    errorHandling,
    isVariableName,
    findVariableIdByName,
    outputParameters,
  ]);

  const hydratedRef = useRef(false);
  const parametersHydratedRef = useRef(false);
  const previousServiceRef = useRef<string>(initialConfig?.serviceId || "");

  useEffect(() => {
    if (
      previousServiceRef.current !== "" &&
      previousServiceRef.current !== selectedService &&
      hydratedRef.current
    ) {
      setSelectedEndpoint("");
      setParameterValues({});
      parametersHydratedRef.current = false;
    }
    previousServiceRef.current = selectedService;
  }, [selectedService]);

  useEffect(() => {
    if (!initialConfig) {
      if (!hydratedRef.current) {
        hydratedRef.current = true;
      }
      return;
    }
    // Only hydrate service/endpoint once
    if (!hydratedRef.current) {
      const shouldHydrateService = !selectedService && initialConfig.serviceId;
      const shouldHydrateEndpoint =
        !selectedEndpoint && initialConfig.endpointId;

      if (shouldHydrateService) setSelectedService(initialConfig.serviceId);
      if (shouldHydrateEndpoint) setSelectedEndpoint(initialConfig.endpointId);

      if (initialConfig.errorHandling)
        setErrorHandling(initialConfig.errorHandling);

      hydratedRef.current = true;
    }

    // Always try to resolve variable names from IDs when variables are available
    // This allows updating values when variables load after initial hydration
    if (
      Array.isArray(initialConfig.inputMappings) &&
      nodeVariables &&
      nodeVariables.length > 0 &&
      !parametersHydratedRef.current
    ) {
      const mapped: Record<string, string> = {};
      initialConfig.inputMappings.forEach((m) => {
        // Si variableName es igual a variableId, probablemente es un ID que no se resolvió
        // Intentar resolverlo a nombre si las variables están disponibles
        let valueToUse = m.variableName;
        if (m.variableId && m.variableName === m.variableId) {
          // Es un ID que no se resolvió, intentar encontrar el nombre
          const resolvedName = findVariableNameById(m.variableId);
          valueToUse = resolvedName;
        } else if (m.variableId) {
          valueToUse = m.variableId;
        }
        mapped[m.parameterId] = valueToUse;
      });
      setParameterValues(mapped);
      parametersHydratedRef.current = true;
    }
  }, [
    initialConfig,
    nodeVariables,
    findVariableNameById,
    selectedEndpoint,
    selectedService,
  ]);

  useEffect(() => {
    const hasServiceAndEndpoint = Boolean(selectedService && selectedEndpoint);
    if (!hasServiceAndEndpoint) {
      onValidationChange(false);
      return;
    }

    const requiredParams = inputParameters.filter((p) => !p.optional);
    const allRequiredFilled = requiredParams.every((p) => {
      const v = parameterValues[p.name];
      return typeof v === "string" && v.trim().length > 0;
    });

    onValidationChange(allRequiredFilled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedService, selectedEndpoint, inputParameters, parameterValues]);

  // Emit config changes only when changed to avoid loops
  const lastEmittedRef = useRef<string>("");
  useEffect(() => {
    if (!(selectedService && selectedEndpoint)) return;
    const next = JSON.stringify(configuration);
    if (lastEmittedRef.current !== next) {
      lastEmittedRef.current = next;
      onConfigChange(configuration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration, selectedService, selectedEndpoint]);

  return (
    <div className="w-full relative flex flex-col items-start box-border text-left text-sm text-darkslategray font-geist">
      <div className="w-full px-4 pt-3 pb-6 self-stretch flex flex-col items-start">
        <div className="w-full flex flex-col items-start gap-1">
          <SelectField
            name="coreService"
            label="Seleccionar Entidad"
            required
            placeholder="Seleccionar..."
            value={selectedService}
            onChange={setSelectedService}
            options={options}
            isLoading={isLoading}
            fullWidth
          />
        </div>
      </div>

      <Renderify condition={!!selectedService}>
        <div className="w-full px-4 pt-3 pb-6 self-stretch flex flex-col items-start border-t border-neutral-quiet">
          <div className="w-full flex flex-col items-start gap-1">
            <SelectField
              name="endpoint"
              label="Acción a realizar"
              required
              placeholder="Seleccionar..."
              value={selectedEndpoint}
              onChange={setSelectedEndpoint}
              options={endpointOptions}
              isLoading={endpointsLoading}
              fullWidth
            />
          </div>
        </div>
      </Renderify>

      <Renderify condition={!!selectedEndpoint}>
        <div className="self-stretch px-4 pt-3 pb-6 flex flex-col items-start border-y border-neutral-quiet">
          <div className="w-full flex flex-col items-start gap-1">
            <h3 className="text-sm font-semibold text-body-loud mb-2">
              Parámetros de Entrada
            </h3>
            {isLoadingVariables ? (
              <div>
                <p className="min-h-[48px] text-body-loud font-semibold flex justify-center items-center py-0.5 px-3 gap-4 ">
                  <Icon
                    type="far"
                    name="spinner-third"
                    className="animate-spin text-xl text-body"
                  />
                </p>
              </div>
            ) : (
              <div className="w-full space-y-4">
                {inputParameters.map((param) => (
                  <div key={param.name} className="flex flex-col gap-2">
                    <ParameterInput
                      param={param}
                      value={parameterValues[param.name] || ""}
                      onChange={(newValue) => {
                        setParameterValues((prev) => ({
                          ...prev,
                          [param.name]: newValue,
                        }));
                      }}
                      variables={nodeVariables}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Renderify>
    </div>
  );
};
